import { useEffect, useRef } from 'react';
import { useAuthStore } from '@/state/store/auth';
import { setRememberMePreference } from '@/state/store/auth/auth-storage';
import { useSigninMutation } from '@/modules/auth/hooks/use-auth';
import { useToast } from '@/hooks/use-toast';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { POST_AUTH_PATH } from '@/lib/auth/org-selection';

function getSsoActivationPath(url: string, provider?: string): string | null {
  const queryPart = url.split('?')[1];
  if (!queryPart) return null;

  const params = new URLSearchParams(queryPart);
  const username = params.get('username');
  const ssoCode = params.get('code');

  return username && ssoCode
    ? `/sso-signup?email=${username}&code=${ssoCode}&provider=${provider}`
    : null;
}

/**
 * Reads `code` and `state` from the URL search params and exchanges them
 * for a session token. Handles deduplication (Apple POST → 302 → GET),
 * MFA redirects, and error feedback.
 */
export function useSsoActivation(provider?: string) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { login, logout: setUnAuthenticated, setTokens } = useAuthStore();
  const { mutateAsync, isPending } = useSigninMutation<'social'>({ suppressToast: true });
  const { toast } = useToast();

  const code = searchParams.get('code');
  const state = searchParams.get('state');
  const effectRan = useRef(false);

  useEffect(() => {
    if (!code || !state) {
      navigate('/login', { replace: true });
      return;
    }

    if (effectRan.current) {
      navigate('/login', { replace: true });
      return;
    }

    effectRan.current = true;

    async function activate() {
      try {
        const res = await mutateAsync({
          grantType: 'social',
          code: code as string,
          state: state as string,
          provider,
        });

        // mutateAsync<'social'> returns SignInResponse | MFASigninResponse
        // But for social login, it typically returns SignInResponse
        const activationPath =
          'sso_user_redirect_url' in res && res.sso_user_redirect_url
            ? getSsoActivationPath(res.sso_user_redirect_url, provider)
            : null;

        if (activationPath) {
          return navigate(activationPath, { replace: true });
        }
        if ('enable_mfa' in res && res.enable_mfa) {
          return navigate(`/verify-mfa?mfa_id=${res.mfaId}&mfa_type=${res.mfaType}`);
        }

        // `cookie_set: true` means IAM authenticated the user and issued the
        // session via Set-Cookie — the same signal the password flow trusts
        // unconditionally (it calls login() on any successful response, token
        // or not). Treat it the same way here instead of requiring a body
        // access_token that this grant type doesn't send.
        const hasSessionCookie = 'cookie_set' in res && !!(res as Record<string, unknown>).cookie_set;

        if (('access_token' in res && res.access_token) || hasSessionCookie) {
          // SSO has no remember-me UI; keep persistent sessions (previous default).
          setRememberMePreference(true);
          login(res.access_token ?? '', res.refresh_token ?? '');
          setTokens({ accessToken: res.access_token ?? '', refreshToken: res.refresh_token ?? '' });
          return navigate(POST_AUTH_PATH, { replace: true });
        }

        // IAM returned 200 but none of the shapes above (no access_token,
        // no cookie_set, no sso_user_redirect_url, no enable_mfa) — surface
        // it instead of silently dropping the user back on /login.
        if (import.meta.env.DEV) {
          console.error('SSO Callback error: unrecognized social/callback response shape', {
            hasAccessToken: 'access_token' in res,
            hasSsoRedirectUrl: 'sso_user_redirect_url' in res,
            enableMfa: 'enable_mfa' in res ? res.enable_mfa : undefined,
            cookieSet: hasSessionCookie,
          });
        }

        navigate('/login', {
          state: { ssoError: t('NO_SUCH_EMAIL_MESSAGE').replace('---', '') },
        });
      } catch (error: any) {
        if (import.meta.env.DEV) {
          console.error('SSO Callback error:', {
            status: error?.status,
            errorCode: error?.error?.error ?? error?.error?.error_code,
          });
        }
        setUnAuthenticated();

        const errorPayloadStr = `${error?.message || ''} ${JSON.stringify(
          error?.error || {}
        )} ${JSON.stringify(error || {})}`.toLowerCase();

        if (errorPayloadStr.includes('state_data_not_found')) {
          toast({
            title: 'ERROR',
            description: t('VERIFICATION_FAILED_PLEASE_TRY_AGAIN'),
            variant: 'destructive',
          });
        } else {
          // if any error, we consider, user is not on the system and signup sso is disabled
          const emailTarget = error?.error?.error_description?.split(' ')[0];
          const errorMsg = emailTarget
            ? t('NO_SUCH_EMAIL_MESSAGE').replace('---', ` (${emailTarget})`)
            : t('NO_SUCH_EMAIL_MESSAGE').replace('---', ``);
          navigate(`/login`, { state: { ssoError: errorMsg } });
          effectRan.current = false;
          return;
        }

        navigate('/login', { replace: true });
        effectRan.current = false;
      }
    }

    activate();
  }, [
    code,
    state,
    mutateAsync,
    navigate,
    login,
    setUnAuthenticated,
    provider,
    toast,
    setTokens,
    t,
  ]);

  return { isPending };
}
